import { Trans } from '@lingui/react/macro';

import { DefectsList } from '@/components/DefectsList';
import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import FoldableSection from '@/design_system/FoldableSection';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { ArticleServices } from '@/routes/Requests/components/ArticleServices/ArticleServices';
import { CommentsSection } from '@/routes/Requests/components/CommentsSection';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useMultiStepCreationContext } from '@/routes/Requests/contexts/MultiStepCreationContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useHasNoAvailableDefectsOrActions } from '@/routes/Requests/New/components/ArticleForm';
import useViewPort from '@/utils/useViewport';

export const NewArticleBodyFormServices = ({
  alertRef,
}: {
  alertRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { isMobile } = useViewPort();
  const { request } = useRequestContext();
  const { article, hasError, errors, view } = useArticleContext();
  const { goToStepById } = useMultiStepCreationContext();
  const { hasNoAvailableActions, hasNoAvailableDefects } = useHasNoAvailableDefectsOrActions();

  const missingArticlePhotoForRefashion = errors.details?.missingArticlePhotoForRefashion;

  return (
    <Stack padding={isMobile ? '1rem' : '1.5rem 2rem'}>
      <Stack
        ref={alertRef}
        style={{
          marginBottom:
            hasError ||
            hasNoAvailableDefects ||
            hasNoAvailableActions ||
            missingArticlePhotoForRefashion
              ? '1rem'
              : undefined,
        }}
        gap="0.5rem"
      >
        {missingArticlePhotoForRefashion && (
          <AlertBar
            type="error"
            title={
              <Trans id="requests.new.articles.alert.missing-article-photo-for-refashion">
                An article photo is required when an action with repair bonus is added.
              </Trans>
            }
          >
            <Button variant="secondary" size="small" onPress={() => goToStepById('information')}>
              <Trans id="requests.new.articles.alert.go-to-information">Go to information</Trans>
            </Button>
          </AlertBar>
        )}
        {hasError && !missingArticlePhotoForRefashion && (
          <AlertBar
            type="error"
            title={
              <Trans id="requests.edit.articles.alert.missing-info">
                Please fill the missing information.
              </Trans>
            }
          />
        )}
        {hasNoAvailableDefects && (
          <AlertBar
            type="warning"
            title={
              <Trans id="requests.edit.articles.alert.no-catalog-defects">
                No defects available for this item. Please add a custom defect.
              </Trans>
            }
          />
        )}
        {hasNoAvailableActions && (
          <AlertBar
            type="warning"
            title={
              <Trans id="requests.edit.articles.alert.no-catalog-actions">
                No services available for this item. Please add a custom service.
              </Trans>
            }
          />
        )}
      </Stack>
      <Stack gap="2rem">
        {view.defects.shown && (
          <FoldableSection
            label={
              <Title as="h2">
                <Trans id="requests.new.articles.drawer.defects.title">Defects</Trans>
              </Title>
            }
            isInvalid={errors.defects?.hasError}
            startClosed={view?.initialCondition.shown}
          >
            <Box padding="1rem" gap="1rem" className="bg-neutral-0">
              <DefectsList />
            </Box>
          </FoldableSection>
        )}
        {view.services.shown && (
          <FoldableSection
            label={
              <Title as="h2">
                <Trans id="requests.new.articles.drawer.services.title">Services</Trans>
              </Title>
            }
            isInvalid={errors.services?.hasError}
          >
            <Box padding="1rem" gap="1rem" className="bg-neutral-0">
              <ArticleServices />
            </Box>
          </FoldableSection>
        )}

        <CommentsSection articleId={article.id} requestId={request.id} />
      </Stack>
    </Stack>
  );
};
